import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  UserPlus, 
  Search,
  Plus,
  User,
  Star,
  Target,
  Loader2,
  Check
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AGE_CATEGORIES = ['All', 'U8', 'U10', 'U12', 'U14', 'U16', 'U18', 'Adults'];
const GENDERS = ['All', 'Boys', 'Girls', 'Mixed'];
const POSITIONS = ['All', 'QB', 'WR', 'RB', 'DB', 'Flex'];

export const FreeAgents = () => {
  const { user } = useAuth();
  const [agents, setAgents] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [ageFilter, setAgeFilter] = useState('All');
  const [genderFilter, setGenderFilter] = useState('All');
  const [positionFilter, setPositionFilter] = useState('All');
  
  // Create agent dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newAgent, setNewAgent] = useState({
    name: '',
    age_category: '',
    gender: '',
    position: 'Flex',
    skills: '',
    contact_email: ''
  });
  
  // Sign agent dialog
  const [showSignDialog, setShowSignDialog] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState('');
  const [signing, setSigning] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [agentsRes, teamsRes] = await Promise.all([
        axios.get(`${API}/free-agents`),
        user ? axios.get(`${API}/teams`) : Promise.resolve({ data: [] })
      ]);
      setAgents(agentsRes.data);
      setTeams(teamsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredAgents = agents.filter(a => {
    const matchesSearch = a.name.toLowerCase().includes(search.toLowerCase());
    const matchesAge = ageFilter === 'All' || a.age_category === ageFilter;
    const matchesGender = genderFilter === 'All' || a.gender === genderFilter;
    const matchesPosition = positionFilter === 'All' || a.position === positionFilter;
    return matchesSearch && matchesAge && matchesGender && matchesPosition;
  });

  const handleCreateAgent = async () => {
    if (!newAgent.name || !newAgent.age_category || !newAgent.gender) {
      toast.error('Please fill in required fields');
      return;
    }

    setCreating(true);
    try {
      const payload = {
        ...newAgent,
        skills: newAgent.skills ? newAgent.skills.split(',').map(s => s.trim()) : []
      };
      await axios.post(`${API}/free-agents`, payload);
      toast.success('Free agent registered!');
      setShowCreateDialog(false);
      setNewAgent({ name: '', age_category: '', gender: '', position: 'Flex', skills: '', contact_email: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to register agent');
    } finally {
      setCreating(false);
    }
  };

  const handleSignAgent = async () => {
    if (!selectedTeam) {
      toast.error('Please select a team');
      return;
    }

    setSigning(true);
    try {
      await axios.post(`${API}/free-agents/${selectedAgent.id}/sign`, { team_id: selectedTeam });
      toast.success(`${selectedAgent.name} signed successfully!`);
      setShowSignDialog(false);
      setSelectedAgent(null);
      setSelectedTeam('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sign player');
    } finally {
      setSigning(false);
    }
  };

  const compatibleTeams = selectedAgent ? teams.filter(t => 
    t.age_category === selectedAgent.age_category &&
    (t.gender === 'Mixed' || t.gender === selectedAgent.gender)
  ) : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="free-agents-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black">
            <span className="text-primary">FREE AGENTS</span>
          </h1>
          <p className="text-muted-foreground mt-1">
            Browse available players and sign them to your team
          </p>
        </div>
        
        {user && (
          <Button 
            className="btn-primary gap-2 mt-4 md:mt-0" 
            onClick={() => setShowCreateDialog(true)}
            data-testid="register-free-agent-btn"
          >
            <Plus className="w-4 h-4" />
            Register Free Agent
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card className="mb-8" data-testid="filters-card">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search players..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>
            
            <Select value={ageFilter} onValueChange={setAgeFilter}>
              <SelectTrigger className="w-full md:w-[140px]" data-testid="age-filter">
                <SelectValue placeholder="Age" />
              </SelectTrigger>
              <SelectContent>
                {AGE_CATEGORIES.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={genderFilter} onValueChange={setGenderFilter}>
              <SelectTrigger className="w-full md:w-[140px]" data-testid="gender-filter">
                <SelectValue placeholder="Gender" />
              </SelectTrigger>
              <SelectContent>
                {GENDERS.map(g => (
                  <SelectItem key={g} value={g}>{g}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={positionFilter} onValueChange={setPositionFilter}>
              <SelectTrigger className="w-full md:w-[140px]" data-testid="position-filter">
                <SelectValue placeholder="Position" />
              </SelectTrigger>
              <SelectContent>
                {POSITIONS.map(p => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Free Agents Grid */}
      {filteredAgents.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAgents.map((agent, index) => (
            <Card 
              key={agent.id} 
              className="card-hover"
              data-testid={`agent-card-${index}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                      <User className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">{agent.name}</h3>
                      <p className="text-sm text-muted-foreground">{agent.position}</p>
                    </div>
                  </div>
                  <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/20">
                    Available
                  </Badge>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex gap-2">
                    <Badge variant="secondary">{agent.age_category}</Badge>
                    <Badge variant="secondary">{agent.gender}</Badge>
                  </div>
                  
                  {agent.skills?.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {agent.skills.map((skill, i) => (
                        <span key={i} className="text-xs px-2 py-1 rounded bg-accent/10 text-accent">
                          {skill}
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {user && (
                  <Button
                    onClick={() => {
                      setSelectedAgent(agent);
                      setShowSignDialog(true);
                    }}
                    className="w-full btn-primary gap-2"
                    data-testid={`sign-agent-${index}`}
                  >
                    <UserPlus className="w-4 h-4" />
                    Sign Player
                  </Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="text-center py-12" data-testid="no-agents">
          <CardContent>
            <User className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
            <h3 className="text-xl font-bold mb-2">No Free Agents Found</h3>
            <p className="text-muted-foreground mb-6">
              {search || ageFilter !== 'All' || genderFilter !== 'All' || positionFilter !== 'All'
                ? 'Try adjusting your filters'
                : 'Be the first to register as a free agent'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Create Agent Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-md" data-testid="create-agent-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-black">REGISTER FREE AGENT</DialogTitle>
            <DialogDescription>
              Add a player to the free agent pool
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Player Name *</Label>
              <Input
                value={newAgent.name}
                onChange={(e) => setNewAgent({ ...newAgent, name: e.target.value })}
                placeholder="John Smith"
                data-testid="agent-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Age Category *</Label>
                <Select 
                  value={newAgent.age_category} 
                  onValueChange={(v) => setNewAgent({ ...newAgent, age_category: v })}
                >
                  <SelectTrigger data-testid="agent-age-select">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {AGE_CATEGORIES.filter(c => c !== 'All').map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Gender *</Label>
                <Select 
                  value={newAgent.gender} 
                  onValueChange={(v) => setNewAgent({ ...newAgent, gender: v })}
                >
                  <SelectTrigger data-testid="agent-gender-select">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Boys">Boys</SelectItem>
                    <SelectItem value="Girls">Girls</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Position</Label>
              <Select 
                value={newAgent.position} 
                onValueChange={(v) => setNewAgent({ ...newAgent, position: v })}
              >
                <SelectTrigger data-testid="agent-position-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {POSITIONS.filter(p => p !== 'All').map(pos => (
                    <SelectItem key={pos} value={pos}>{pos}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Skills (comma separated)</Label>
              <Input
                value={newAgent.skills}
                onChange={(e) => setNewAgent({ ...newAgent, skills: e.target.value })}
                placeholder="Fast, Good hands, Route runner"
                data-testid="agent-skills-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Contact Email</Label>
              <Input
                type="email"
                value={newAgent.contact_email}
                onChange={(e) => setNewAgent({ ...newAgent, contact_email: e.target.value })}
                placeholder="player@email.com"
                data-testid="agent-email-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateAgent}
              disabled={creating}
              className="btn-primary gap-2"
              data-testid="submit-agent-btn"
            >
              {creating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Registering...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Register
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sign Agent Dialog */}
      <Dialog open={showSignDialog} onOpenChange={setShowSignDialog}>
        <DialogContent className="sm:max-w-md" data-testid="sign-agent-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-black">SIGN PLAYER</DialogTitle>
            <DialogDescription>
              Sign {selectedAgent?.name} to your team
            </DialogDescription>
          </DialogHeader>
          
          {selectedAgent && (
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-muted/50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                    <User className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-bold">{selectedAgent.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {selectedAgent.position} • {selectedAgent.age_category} • {selectedAgent.gender}
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Select Team</Label>
                <Select value={selectedTeam} onValueChange={setSelectedTeam}>
                  <SelectTrigger data-testid="select-team-for-signing">
                    <SelectValue placeholder="Choose a team" />
                  </SelectTrigger>
                  <SelectContent>
                    {compatibleTeams.length > 0 ? (
                      compatibleTeams.map(team => (
                        <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                      ))
                    ) : (
                      <SelectItem value="none" disabled>No compatible teams</SelectItem>
                    )}
                  </SelectContent>
                </Select>
                {compatibleTeams.length === 0 && (
                  <p className="text-xs text-destructive">
                    No teams match this player's age/gender category
                  </p>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSignDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSignAgent}
              disabled={signing || !selectedTeam || compatibleTeams.length === 0}
              className="btn-primary gap-2"
              data-testid="confirm-sign-btn"
            >
              {signing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Signing...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4" />
                  Sign Player
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
